import { ApolloError } from '@apollo/client'
import { ItemStatus } from '@engyalo/delivery-ui-components/lib/interfaces/enums'
import {
  SubItem,
  SubItems,
} from '@engyalo/delivery-ui-components/lib/interfaces/catalog'
import {
  ActivePromotion,
  IPackage,
  IPagination,
  IProductCatalog,
  IPromotion,
  PromotionSelectionCriteria,
} from '../graphQL/commons/types'
import {
  IPromotionShortcut,
  IPromotionsAvailability,
  IPromotionUiCatalog,
} from './promotions'
import { IConfig } from './config'
import { ICartProduct } from './cart'

export type TBundle = Record<string, number>

export type TBundleComplete = Record<string, boolean>

export interface ICustomFields {
  isAlcohol: string
  Returnable: string
  MaximumCases: string
}
export interface ILinkedConditionalStock {
  requiredQuantity: number
  stockPerRequiredQuantity: number
}

export interface IPromotionDetails {
  mainText: string
  secondaryText?: string
  savingText?: string
}
export interface IPromotionsDetails {
  title: string
  secondaryTitle: string
  details: IPromotionDetails[]
}

export interface IBundleExtraSanitized {
  /**
   * Name of the product
   * */
  name: string
  /**
   * sku of the product
   * */
  sku: string
  /**
   * stock of the product
   * */
  stock: number
  /**
   * product description suffix label
   * */
  suffixLabel?: string
  /**
   * product price
   * */
  price: number | null
}

export interface IPackageProduct extends IPackage {
  suffix: string
}

export interface IVolumePromotionDetail {
  id: string
  quantity: string
  savings?: string
  price: string
  /**
   * Optional per-row text forwarded from the source promotion's `description`
   * (typically populated by an upstream tenant add-on). Only present when the
   * source supplies a non-empty description — an empty or absent value omits the
   * key. Intended for the library Table's optional `description` column.
   */
  description?: string
}

export interface IVolumeResume {
  selectedId?: string
  promotions: IVolumePromotionDetail[]
}

/** The product measures a rounding rule can be built from. */
export const ROUNDING_RULE_TYPES = ['layer', 'pallet'] as const

/** Which product measure a rounding rule was built from. */
export type RoundingRuleType = (typeof ROUNDING_RULE_TYPES)[number]

export interface IRoundingRules {
  thresholdUp: number
  value: number
  thresholdDown: number
  /**
   * The measure this rule rounds to. The counter hands the breached rule back
   * as-is, so this is how the cart alert tells a layer rounding from a pallet
   * one. Optional so a rule built without it still type-checks; such a rule
   * reads as untyped and gets the generic message.
   */
  type?: RoundingRuleType
}

export interface IProduct
  extends Omit<IProductCatalog, 'bundlesExtra' | 'jumpQty' | 'extraPackages' | 'layerQty' | 'palletQty' | 'layerDown' | 'layerUp' | 'palletDown' | 'palletUp'> {
  promotion?: IPromotionUiCatalog
  quantity: number
  subtotal: number
  promotionalQty: number
  maxQtyAllowed?: number
  minQtyAllowed: number
  inCartQuantity?: number
  promotionsDetails?: IPromotionsDetails
  promotionsShortcuts?: IPromotionShortcut[]
  isBundle?: boolean
  subItems?: SubItems
  bundlesExtra: IBundleExtraSanitized[]
  step?: number
  extraPackages: IPackageProduct[]
  subItem?: SubItem
  prefilledQuantity?: number | null
  /**
   * Volume promotions resume
   */
  volumePromotionsResume?: IVolumeResume
  roundingRules?: IRoundingRules[]
  /**
   * Client-only flag, true while an add, decrement or delete mutation for this
   * sku is in flight, and only when `globals.counter.disableWhileSyncing` is
   * enabled. Written by `setSkuSyncing` (`redux/skuSyncing.ts`) onto
   * `catalogSlice.selectedProduct` and onto the list rows of the slices that
   * handle that action. `setProductDetail` writes it too: called with
   * `keepSyncing` for the product already open it keeps the live value, and
   * otherwise it resets it to `undefined`.
   *
   * Tri-state: `undefined` means never marked, or rebuilt since; `false` means
   * marked and settled. Both mean not syncing, so read it as `=== true` or as
   * a truthy test, never as `=== false` or a key-presence check.
   *
   * Consumers: the product detail Counter's `disabled` binding, and the
   * library ItemCard, which passes `isSyncing || counter.disabled` to a list
   * row's Counter as `disabled`. The quantity resync happens on the
   * `true -> false` edge, which depends on the counter keeping
   * `updateValueOnDisabled: false` (the UI library defaults it to `true`). A
   * counter that sets it to `true` makes the resync fire when the request
   * *starts* too, discarding what the user typed before the server answered.
   * On list rows ItemCard 1.23.1 substitutes `updateValueOnDisabled: false`
   * while syncing when the prop is unset, so only an explicit `true` breaks
   * them.
   */
  isSyncing?: boolean
}

export interface ICategory {
  slug: string
  image: string
  category: string | null
  name: string | null
}

export interface ICatalogItem {
  id?: string
  category?: string | null
  productsPrices?: IProduct[] | null
  slug?: string
  order?: number
  parentId?: string
}

export interface ISearchResult {
  items?: IProduct[]
  loading?: boolean
  counter?: number
  status?: 'OK' | 'LOADING' | 'ERROR'
  error?: ApolloError
  term?: string
}

export interface ISearchData extends ISearchResult {
  totalSearchStartCount: number
}

export interface ICategoryPagination extends IPagination {
  hasMore: boolean
}

export interface IFreegoodsConf {
  isConfirmDisabled: boolean
  promotion?: IPromotion
  freegoods: IFreeGood[]
  selectBy: PromotionSelectionCriteria
  quantitySelection?: IFreegoodsQuantitySelection
  maxQuantity?: number
}

export interface ICatalog {
  categories: ICatalogItem[]
  categoryList: ICategory[]
  currentCategory?: ICatalogItem
  isFetchingMore: boolean
  search: ISearchData
  pagination: {
    [category: string]: ICategoryPagination
  }
  selectedProduct?: IProduct
  synchronizingCart: boolean
  categoriesPagination?: ICategoryPagination
  recommendedProducts?: IProduct[]
  categorySelectedStatus?: ItemStatus
  bundlesCount?: TBundle
  disabledAddOnBundle?: boolean
  currentBundleCount?: number
  bundleCompleted?: boolean
  subItemEdit?: SubItem | null
  subItemsList?: SubItems
  freegoodsConf: IFreegoodsConf
  currentBundlePrice: string | null
  currentBundlePriceValue: number
  currentBundleSubtotal: number
  currentCategoryCatalog?: string
  /**
   * The stock lookup's verdict on the selected product's promotion add-all
   * buttons. Written only under `globals.promotions.disableUnfulfillableAddAll`.
   */
  promotionsAvailability?: IPromotionsAvailability
  /**
   * True while `templateActions.catalog.refreshCatalog` is writing new prices
   * (COM-1500). Bound to `itemCard.noMemoize` on the cart list, the catalogue
   * lists, the recommended-products carousels and `ItemListSuggestedProducts`:
   * the library's ItemCard memo does not compare price, so without it a
   * repriced row would keep its old price.
   */
  repricing?: boolean
  /**
   * True once `templateActions.catalog.refreshCatalog` has written new prices
   * (COM-1520). Every catalogue page the Apollo cache holds predates them, so
   * load-more then reads with `no-cache` (`useProductsLazyLoader`). Never
   * lowered: a later refresh outdates the pages read before it in the same
   * way.
   */
  repriced?: boolean
}

export interface IFreegoodNameDescription {
  name: string
  description: string
}

export interface IFreeGood {
  id: string
  imageUrl?: string
  namesDescriptions: IFreegoodNameDescription[]
  isChecked: boolean
}

export interface IFreegoodsQuantitySelectionGroup {
  identifier: string | null
  maxTotalQuantity: number
  products: IProduct[]
}

export interface IFreegoodsQuantitySelection {
  groups: IFreegoodsQuantitySelectionGroup[]
  products: IProduct[]
  maxTotalQuantity: number
  totalSelected: number
}

export interface IGetPromotionDetailsArgs {
  quantity?: number
  promotion: IPromotion
  config?: IConfig
  activePromotion?: ActivePromotion
  price?: number
}

export type GetPromotionsUiDetails = (
  args: IGetPromotionDetailsArgs,
) => IPromotionUiCatalog | undefined
